import { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { buildUrlWithParams, getAllMetadataParams, useQueryParams } from '@hooks/useQueryParams';
import { BOOKING_STATUS_PENDING } from '@models/Booking';
import { useCheckoutActions, useCheckoutState } from '@services/checkoutClient';
import { useSharedDataActions, useSharedDataState } from '@store/sharedData';
import { getLocaleFromLang } from '@utils/get-locale-from-lang';
import PropTypes from 'prop-types';

import { usePaymentForm } from '@xceedsrl/adyenjs';

import { BookingTerms } from './BookingTerms';

export const Payment = ({
  bookingId,
  channel,
  booking,
  offer,
  event,
  club,
  hasToPay,
  setValid,
  forRef,
  shoppingCart,
  marketingGroupConditionsRef,
  isFormValid,
}) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { addPaymentResult, setApp18voucher } = useCheckoutActions();
  const { participants, metadata } = useSharedDataState();
  const { configuration } = useCheckoutState();
  const { setAdyenCheckout } = useSharedDataActions();
  const [areMarketingAccepted, setAreMarketingAccepted] = useState(false);
  const query = useQueryParams();
  const offerId = query.get('offerId');
  const eventId = query.get('eventId');
  const redirectUrl = query.get('redirectUrl');
  const metadataQuery = getAllMetadataParams(query);
  const finalMetadata = metadataQuery || metadata;

  const onFailure = error => {
    const errorType = error ? error.success : 'paymenterror';
    const url = buildUrlWithParams(`/${channel}/error/${offer?.id ?? offerId}/${errorType}`, {
      eventId,
      redirectUrl: redirectUrl || configuration.redirectUrl,
      metadataString: finalMetadata,
    });
    history.push(url);
  };

  function onFailureLogging(error) {
    if (error.name.toLowerCase() === 'cancel' || error === null) onFailure(error);
  }

  const onSuccess = (response, bookingIdResp) => {
    if (response.resultCode === 'Refused') {
      onFailure(null);
      return;
    }
    addPaymentResult({ bookingId: bookingIdResp });

    const urlParamRedirectUrl = configuration.redirectUrl
      ? `redirectUrl=${configuration.redirectUrl}`
      : '';
    history.push(
      `/${channel}/booking/${bookingId}?${urlParamRedirectUrl}${
        eventId ? `&eventId=${eventId}` : ''
      }`
    );
  };

  const lang = i18n.language;
  const locale = getLocaleFromLang(lang);

  const bookingData = {
    rsvp: offer.id,
    type: offer.admissionType,
    source: 'web',
    numTickets: booking.bookingQuantity,
    price: shoppingCart.total,
    fee: offer.commissions.transactionFee,
    eventId: event.legacyId,
    venueId: event.venue.legacyId,
    venueName: event.venue.name,
    promoterSlug: channel || 'xceed-web',
    attendesForPurchase: offer.guestCapacity
      ? offer.guestCapacity * booking.bookingQuantity
      : booking.bookingQuantity,
    bookingHoldToken: null,
    bookingId,
  };

  const [PaymentForm, submit] = usePaymentForm({
    config: {
      apiHost: `${process.env.REACT_APP_PUBLIC_LEGACY_API_HOST}/b2c/v4`,
      checkoutHost: process.env.REACT_APP_PUBLIC_PAYMENTS_SERVICE_HOST,
      clientKey: process.env.REACT_APP_ADYEN_CLIENT_KEY,
      environment: process.env.REACT_APP_ADYEN_ENVIRONMENT,
    },
    paymentOptions: {
      country: club.city?.country.isoCode,
      amount: shoppingCart.total,
      hasToPay,
      currency: offer.price.currency,
      storePaymentMethod: false,
      paymentAccountId: null,
      googlePayConfiguration: {
        merchantName: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME,
        gatewayMerchantId: process.env.REACT_APP_GOOGLE_PAY_GATEWAY_MERCHANT_ID,
        merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID,
      },
    },
    checkoutOptions: { locale },
    dropinProps: {
      lang,
      bookingData,
      onSuccess,
      onFailure,
      onFailureLogging,
      onValidate: setValid,
      is18appEnabled: !!offer.settings?.italyRegulation?.allows18App,
    },
    participants,
    setAdyenCheckout,
    setApp18voucher,
    app18voucher: shoppingCart.app18voucher,
  });

  useImperativeHandle(forRef, () => ({
    handleSubmitFromParent() {
      const submitData = {
        ...(shoppingCart.app18voucher && { app18voucher: shoppingCart.app18voucher }),
        ...(areMarketingAccepted && { marketingConditionsAccepted: true }),
      };
      submit(submitData);
    },
  }));

  if (booking.status !== BOOKING_STATUS_PENDING) {
    const url = buildUrlWithParams(`/${channel}/error/${offer.id}/${booking.status}`, {
      eventId,
      redirectUrl: redirectUrl || configuration.redirectUrl,
      metadataString: finalMetadata,
    });
    history.push(url);
  }

  return (
    <>
      <form>{PaymentForm}</form>
      <BookingTerms
        company={event.company}
        buttonText={t('common:pay')}
        areMarketingAccepted={areMarketingAccepted}
        setAreMarketingAccepted={setAreMarketingAccepted}
        marketingGroupConditionsRef={marketingGroupConditionsRef}
        isValidRestOfForm={isFormValid}
      />
    </>
  );
};

Payment.propTypes = {
  booking: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  club: PropTypes.object.isRequired,
  bookingId: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
  hasToPay: PropTypes.bool.isRequired,
  setValid: PropTypes.func.isRequired,
  forRef: PropTypes.any.isRequired,
  shoppingCart: PropTypes.object.isRequired,
  marketingGroupConditionsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(HTMLDivElement) }),
  ]).isRequired,
  isFormValid: PropTypes.bool.isRequired,
};
